"use client";

import { Mountain, Coffee, Route, Satellite, Landmark, Tent } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyChooseUs() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    index: "01",
    icon: Landmark,
    title: "UAE Registered Company",
    body: "Professional business operations, contracts, invoicing, and communication to ensure your brand's security and compliance."
  }, {
    index: "02",
    icon: Mountain,
    title: "Industry Experience",
    body: "Built by creators with firsthand experience in influencer marketing and brand partnerships across global platforms."
  }, {
    index: "03",
    icon: Route,
    title: "Proven Results",
    body: "Experience working with brands across gaming, digital services, and online platforms to deliver measurable growth."
  }, {
    index: "04",
    icon: Satellite,
    title: "Direct Creator Access",
    body: "Relationships with creators across multiple niches and audience sizes, ensuring the perfect match for your brand."
  }, {
    index: "05",
    icon: Coffee,
    title: "Save Time",
    body: "We handle creator sourcing, outreach, negotiations, and campaign management so you can focus on growth."
  }, {
    index: "06",
    icon: Tent,
    title: "Strategic Execution",
    body: "Data-driven campaign management that ensures your product gets in front of the right audience at scale."
  }];
  return <section data-section-id="1437" id="why-choose-us" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden">
      {}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-primary/5 border-t border-border overflow-hidden flex items-center">
        <div className="flex whitespace-nowrap animate-[ticker_40s_linear_infinite]">
          {[0, 1].map(dup => <div key={dup} data-index={dup} className="flex shrink-0">
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">STRATEGY</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">CREATORS</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">GROWTH</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">SCALE</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">RESULTS</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground px-8">PARTNERSHIPS</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={headRef} className={`lg:sticky lg:top-32 relative transition-all duration-[700ms] ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              {}
              <div className="pointer-events-none select-none absolute -top-8 -left-2 font-default italic font-light text-primary/[0.08] leading-none" style={{
              fontSize: "clamp(8rem, 15vw, 15rem)"
            }} aria-hidden="true">
                03
              </div>

              {}
              <p className="relative font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-10">§ 02 · WHY PINE PLAYERZ MEDIA</p>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="w-1.5 h-1.5 bg-primary rounded-lg" />
              </div>

              {}
              <h2 className="relative font-default font-light text-foreground leading-[0.96] tracking-[-0.025em] mb-10" style={{
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)"
            }}>
                Why brands <span className="italic text-primary font-light">choose us.</span>
              </h2>

              {}
              <p className="relative max-w-[320px] text-muted-foreground leading-[1.55] mb-12" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
            }}>
                We bridge the gap between corporate scalability and raw digital influence. Our approach is built on precision, transparency, and a deep understanding of the creator economy.
              </p>

              {}
              
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <ul className="flex flex-col">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <BenefitRow key={i} i={i} benefit={benefit} Icon={Icon} isLast={i === benefits.length - 1} />;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function BenefitRow({
  i,
  benefit,
  Icon,
  isLast
}: {
  i: number;
  benefit: {
    index: string;
    title: string;
    body: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li ref={ref} data-index={i} className={`group relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-10 md:py-12 transition-all duration-[700ms] ${i !== 0 ? "border-t border-border" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${i * 90}ms`
  }}>
      {}
      <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-6">
        <span className="font-mono text-xs tracking-[0.14em] uppercase text-primary">
          {benefit.index}
        </span>
        <div className="relative w-6 h-6 flex items-center justify-center">
          <Icon className="w-6 h-6 text-primary transition-all duration-[260ms] ease-out group-hover:opacity-70" strokeWidth={1.25} />
        </div>
      </div>

      {}
      <div className="md:col-span-10">
        <h3 className="font-default font-normal text-foreground leading-[1.15] tracking-[-0.015em] mb-5 transition-transform duration-[260ms] ease-out group-hover:-translate-y-1" style={{
        fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)"
      }}>
          {benefit.title}
        </h3>
        <p className="max-w-[480px] text-muted-foreground leading-[1.6] mb-6" style={{
        fontSize: "clamp(0.9375rem, 1vw, 1rem)"
      }}>
          {benefit.body}
        </p>

        {}
        {!isLast && <div className="flex items-center">
            <div className="h-px w-20 bg-primary" />
            <div className="w-1.5 h-1.5 bg-primary rounded-lg" />
          </div>}
      </div>
    </li>;
}